import { Button, Select, Tabs } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useState, useEffect } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { CaretLeftFilled } from '@ant-design/icons';

import AudioPlayButton from './audio-play-button';
import getLabelDisplayName from 'utils/label-display';

const keyToLongQuestion: { [attrName: string]: string } = {
    race: 'What is their perceived race or ethnicity? Please select all that apply',
    age: 'What is their perceived age?',
    sescar: 'What is the perceived socio-economic status as evidenced by their car make/model/condition?',
    sesclothing: 'What is the perceived socio-economic status as evidenced by their clothing?',
    gender: 'What is their perceived gender?',
    height: 'What is their perceived height?',
    bodytype: 'What is their perceived body type?',
    homelessness: 'Does CX exhibit any signs of homeless circumstances (eg the car is full of living material)?',
    foreignaccent: 'Does CX/POX have a non-American accent?',
    regionalaccent: 'Does CX/POX have a regional American accent?',
    lgbtq: 'Is CX/POX perceived to be a part of the LGBTQ community?',
    dui: 'Does CX/POX exhibit any signs of being under the influence of drugs or alcohol?',
    specialneeds:
        'Does CX/POX exhibit any signs of special needs (e.g. physical disabilities, blindness, autism, etc. as defined in the special needs training. Leave blank if Not.) ?',
    'phase1b.percentageCommunication':
        'What share of the total police-driver communication that occurred during this stop is captured on this video?',
};

interface Props {
    croppedImages: any;
    audioBlobURL: string;
    states: any[];
    audio: React.MutableRefObject<HTMLAudioElement[]>;
    playAudio: (idx: number) => void;
    audiolisteners: any;
    audioPlayingIdx: number;
    frameSpeed: number;
    setAudioPlayingIdx: (idx: number) => void;
    setDoneButtonDisabled: (disabled: boolean) => void;
    onUpdateAnnotations: (states: any[]) => void;
    onUpdateJobAttributes(id: number, value: string): void;
    jobAttributeSpec: any[];
    jobAttributes: any;
    beforeSubmit: boolean;
}

function Phase1bQuestionnaire(props: Props) {
    const {
        states,
        audioBlobURL,
        audio,
        croppedImages,
        audiolisteners,
        audioPlayingIdx,
        frameSpeed,
        beforeSubmit,
        jobAttributeSpec,
        jobAttributes,
        playAudio,
        setAudioPlayingIdx,
        setDoneButtonDisabled,
        onUpdateAnnotations,
        onUpdateJobAttributes,
    } = props;
    useEffect(() => {
        setDoneButtonDisabled(true);
    }, []);

    useEffect(() => {
        setDoneButtonDisabled(false);
        return () => setDoneButtonDisabled(true);
    }, []);

    return (
        <Tabs
            type='card'
            tabBarStyle={{ marginBottom: '0px' }}
            onChange={() => {
                audio.current.forEach((rf) => rf.pause());
                setAudioPlayingIdx(-1);
            }}
        >
            {states.map((state, idx) => (
                <Tabs.TabPane
                    tab={
                        <span>
                            <Text>
                                {getLabelDisplayName(state.label.name)}{' '}
                                {
                                    state.attributes[
                                        state.label.attributes?.find((attr: any) => attr.name === 'identifier').id
                                    ]
                                }
                            </Text>
                            {state.label.attributes
                                .filter((attr: any) => attr.name.startsWith('demographics.') && !attr.nullable)
                                .some((attr: any) => state.attributes[attr.id] === '') && (
                                <CaretLeftFilled style={{ color: 'red' }} />
                            )}
                        </span>
                    }
                    key={state.clientID}
                >
                    <Row gutter={16}>
                        <Col span={18}>
                            {state.label.attributes
                                .filter((attr: any) => attr.name.startsWith('demographics.'))
                                .toSorted((a: any, b: any) => a.id - b.id)
                                .map((attr: any) => (
                                    <div>
                                        <div style={{ margin: '25px 0 0 0' }}>
                                            <Text>{keyToLongQuestion[attr.name.split('demographics.')[1]]}</Text>{' '}
                                            {state.attributes[attr.id] === '' && !attr.nullable && (
                                                <CaretLeftFilled style={{ color: 'red' }} />
                                            )}
                                        </div>
                                        <div>
                                            {attr.inputType === 'select' && (
                                                <Select
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val) => {
                                                        state.attributes = {
                                                            ...state.attributes,
                                                            [attr.id]: val,
                                                        };
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    value={state.attributes[attr.id]}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'multiselect' && (
                                                <Select
                                                    mode='multiple'
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val: string[]) => {
                                                        state.attributes = {
                                                            ...state.attributes,
                                                            [attr.id]: val
                                                                .map((valitem: string) => valitem.trim())
                                                                .join(),
                                                        };
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    value={
                                                        state.attributes[attr.id]
                                                            ? state.attributes[attr.id]
                                                                  .split(',')
                                                                  .filter((valitem: string) => valitem.trim() !== '')
                                                            : []
                                                    }
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </Col>
                        <Col span={6}>
                            {states[idx].objectType === 'audioselection' && audioBlobURL && (
                                <>
                                    <AudioPlayButton
                                        audioBlobURL={audioBlobURL}
                                        audio={audio}
                                        audioPlayingIdx={audioPlayingIdx}
                                        frameSpeed={frameSpeed}
                                        audiolisteners={audiolisteners}
                                        idx={idx}
                                        playAudio={playAudio}
                                        setAudioPlayingIdx={setAudioPlayingIdx}
                                        state={states[idx]}
                                    />
                                </>
                            )}
                            {states[idx].objectType === 'shape' && croppedImages[idx] && (
                                <img style={{ maxWidth: '100%', height: '150px' }} src={croppedImages[idx]}></img>
                            )}
                        </Col>
                    </Row>
                </Tabs.TabPane>
            ))}

            {beforeSubmit && jobAttributeSpec.filter((spec) => spec.name.startsWith('phase1b')).length && (
                <Tabs.TabPane
                    tab={
                        <span>
                            <Text>Situation Labels</Text>
                            {jobAttributeSpec
                                .filter((spec) => spec.name.startsWith('phase1b'))
                                .some((attr: any) => jobAttributes[attr.id] === '') && (
                                <CaretLeftFilled style={{ color: 'red' }} />
                            )}
                        </span>
                    }
                    key={9999999999}
                >
                    <Row gutter={16}>
                        <Col span={18}>
                            {jobAttributeSpec
                                .filter((attr: any) => attr.name.startsWith('phase1b.'))
                                .toSorted((a: any, b: any) => a.id - b.id)
                                .map((attr: any) => (
                                    <div>
                                        <div style={{ margin: '25px 0 0 0' }}>
                                            <Text>{keyToLongQuestion[attr.name]}</Text>{' '}
                                            {jobAttributes[attr.id] === '' && (
                                                <CaretLeftFilled style={{ color: 'red' }} />
                                            )}
                                        </div>
                                        <div>
                                            {attr.inputType === 'select' && (
                                                <Select
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val) => {
                                                        onUpdateJobAttributes(attr.id, val);
                                                    }}
                                                    value={jobAttributes[attr.id]}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'multiselect' && (
                                                <Select
                                                    mode='multiple'
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val: string[]) => {
                                                        onUpdateJobAttributes(
                                                            attr.id,
                                                            val.map((valitem: string) => valitem.trim()).join(),
                                                        );
                                                    }}
                                                    value={
                                                        jobAttributes[attr.id]
                                                            ? jobAttributes[attr.id]
                                                                  .split(',')
                                                                  .filter((valitem: string) => valitem.trim() !== '')
                                                            : []
                                                    }
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </Col>
                    </Row>
                </Tabs.TabPane>
            )}
        </Tabs>
    );
}

export default Phase1bQuestionnaire;
